import env from './env';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const PLUS_ICON_URL = `${env.baseUrl}${env.externalPath}/icons/plus.svg`;

function createSvgElement(name, attributes) {
  const element = document.createElementNS(SVG_NAMESPACE, name);
  Object.entries(attributes).forEach(([attribute, value]) => element.setAttribute(attribute, String(value)));
  return element;
}

function createDecorativePlus(outline) {
  const svg = outline.ownerSVGElement;
  if (!svg || outline.nextElementSibling?.classList.contains('interactive-image-map-plus')) return;

  let bounds;
  try {
    bounds = outline.getBBox();
  } catch {
    return;
  }

  if (
    !bounds ||
    ![bounds.x, bounds.y, bounds.width, bounds.height].every(Number.isFinite) ||
    bounds.width <= 0 ||
    bounds.height <= 0
  ) {
    return;
  }

  const centerX = bounds.x + bounds.width / 2;
  const centerY = bounds.y + bounds.height / 2;
  const size = Math.min(bounds.width, bounds.height) * 0.72;
  if (!Number.isFinite(centerX) || !Number.isFinite(centerY) || size <= 0) return;

  const plus = createSvgElement('g', {
    class: 'interactive-image-map-plus',
    'aria-hidden': 'true',
    'pointer-events': 'none',
    transform: `translate(${centerX} ${centerY})`,
  });
  plus.append(
    createSvgElement('circle', { class: 'interactive-image-map-plus-background', r: size / 2 }),
    createSvgElement('image', {
      class: 'interactive-image-map-plus-glyph',
      href: PLUS_ICON_URL,
      x: -size / 2,
      y: -size / 2,
      width: size,
      height: size,
    })
  );
  outline.after(plus);
}

function createPersistentLabel(outline, interactiveImageMap) {
  const title = outline.getAttribute('aria-label')?.trim();
  if (!title) return null;

  const existingLabel = Array.from(interactiveImageMap.querySelectorAll('.interactive-image-map-label')).find(
    (label) => label.dataset.interactiveRegionId === outline.dataset.interactiveRegionId
  );
  if (existingLabel) return existingLabel;

  const label = document.createElement('span');
  label.className = 'interactive-image-map-label';
  label.dataset.interactiveRegionId = outline.dataset.interactiveRegionId;
  label.setAttribute('aria-hidden', 'true');
  label.textContent = title;
  interactiveImageMap.append(label);
  return label;
}

(function () {
  const interactiveImageMaps = document.querySelectorAll('.interactive-image-map');
  if (!interactiveImageMaps.length) return;

  interactiveImageMaps.forEach((interactiveImageMap) => {
    const outlines = interactiveImageMap.querySelectorAll('.outline');
    const desktopPreviewMedia = window.matchMedia?.('(min-width: 992px) and (hover: hover) and (pointer: fine)');
    const labels = Array.from(outlines, (outline) => ({
      outline,
      label: createPersistentLabel(outline, interactiveImageMap),
    })).filter(({ label }) => label);

    const positionLabels = () => {
      const mapBounds = interactiveImageMap.getBoundingClientRect();

      labels.forEach(({ outline, label }) => {
        const outlineBounds = outline.getBoundingClientRect();
        label.style.left = `${outlineBounds.left - mapBounds.left + outlineBounds.width / 2}px`;
        label.style.top = `${outlineBounds.bottom - mapBounds.top}px`;
      });
    };

    const updatePresentationMode = () => {
      const desktopMode = !desktopPreviewMedia || desktopPreviewMedia.matches;
      interactiveImageMap.classList.toggle('is-persistent-label-mode', !desktopMode);

      const Popover = window.bootstrap?.Popover;
      if (!Popover) return;

      outlines.forEach((outline) => {
        const instance = Popover.getInstance(outline);
        const title = outline.getAttribute('aria-label')?.trim();
        const description = outline.dataset.previewDescription?.trim();
        const hasContent = title || description;

        if (!desktopMode || !hasContent) {
          instance?.dispose();
          return;
        }

        if (!instance) {
          new Popover(outline, {
            trigger: 'hover focus',
            placement: 'bottom',
            offset: [0, 12],
            container: interactiveImageMap,
            title: title || '',
            content: description || '',
          });
        }
      });
    };

    outlines.forEach(createDecorativePlus);
    positionLabels();
    window.addEventListener('resize', positionLabels);
    desktopPreviewMedia?.addEventListener?.('change', updatePresentationMode);
    if (window.bootstrap?.Popover) updatePresentationMode();
    else queueMicrotask(updatePresentationMode);

    // wistia video play handler
    interactiveImageMap.querySelectorAll('[data-wisita-id]').forEach((wisitaPath) => {
      wisitaPath.addEventListener('click', () => {
        if (wisitaPath.dataset.wisitaId) {
          interactiveImageMap.parentElement
            .querySelector('#' + wisitaPath.dataset.wisitaId)
            .querySelector('.wistia_click_to_play')
            ?.click();
        }
      });
    });

    // Add keyboard interaction for paths
    outlines.forEach((element) => {
      element.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          element.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        } else if (event.key === 'Escape') {
          window.bootstrap?.Popover.getInstance(element)?.hide();
        }
      });
    });
  });
})();
